/**
 * Centralized auth redirect URL builder.
 * All Supabase auth redirects MUST use this to ensure:
 * - Trailing slashes are stripped (prevents double-slash URLs)
 * - All auth flows route through /auth/callback for PKCE code exchange
 * - Consistent fallback URL across the codebase
 */

import { getBaseUrl } from '@/lib/email/shared'

// Re-export for convenience so auth consumers don't import from email module
export { getBaseUrl }

/**
 * Build a redirect URL that routes through /auth/callback for PKCE code exchange.
 * After code exchange, user is redirected to `next` path.
 *
 * @param next - Path to redirect to after code exchange (e.g. '/auth/reset-password')
 * @param extraParams - Additional query params (e.g. { flow: 'login', org: 'ogc' })
 * @returns Full URL like 'https://alumnia.net/auth/callback?next=/auth/reset-password'
 */
export function getAuthCallbackUrl(next?: string, extraParams?: Record<string, string>): string {
  const base = getBaseUrl()
  const params = new URLSearchParams()

  if (next) {
    params.set('next', next)
  }

  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      params.set(key, value)
    }
  }

  const queryString = params.toString()
  return `${base}/auth/callback${queryString ? `?${queryString}` : ''}`
}

/**
 * Allowed `type` values for the token-hash confirm flow.
 * Extend only when the corresponding email template + handler logic ships.
 *
 * - `recovery`  — password reset (live since v1.71).
 * - `magiclink` — self-serve org creation (`/start`). Mirrors recovery: the
 *   link is inert until the Supabase "Magic Link" email template is flipped to
 *   embed `{{ .TokenHash }}` → `/auth/confirm`. See
 *   `.planning/runbooks/auth-recovery.md` § Self-serve org creation.
 *
 * The sign-up confirmation (ALU-280) is not in the list on purpose: it is
 * verified only by the POST of `/auth/confirm`, from the button of the
 * `/auth/confirm-email` landing page, so a mail scanner's GET cannot spend it.
 * Recovery and magic link are verified the same way (ALU-292): their GET only
 * forwards to `/auth/confirm-link`, and the POST from its button verifies.
 */
export const AUTH_CONFIRM_TYPES = ['recovery', 'magiclink'] as const
export type AuthConfirmType = (typeof AUTH_CONFIRM_TYPES)[number]

/**
 * Single source of truth for the URL `errorCode` emitted when a recovery
 * link fails verification (expired, single-use already consumed, missing/
 * unsupported type, etc.). Consumed by `app/login/login-form.tsx` to swap
 * the dead-end banner for an actionable "Send a fresh reset link" CTA.
 */
export const RECOVERY_LINK_FAILED_CODE = 'recovery_link_failed'

/**
 * Sibling of {@link RECOVERY_LINK_FAILED_CODE} for the `/start` magic-link flow.
 * When the self-serve confirmation link fails verification (expired, already
 * consumed by an email prefetch, superseded by a resend), `/auth/confirm`
 * emits this code and `app/login/login-form.tsx` renders an actionable
 * "request a new link" CTA pointing back to `/start` — never the password-reset
 * CTA (a brand-new self-serve user has no password yet).
 */
export const MAGIC_LINK_FAILED_CODE = 'magic_link_failed'

/**
 * The closed set of `?error=` codes `/login` renders (ALU-518). The OAuth
 * callback emits one of these codes; the login screens translate it from the
 * `auth` namespace. Any other value, such as text pasted into a crafted link,
 * is ignored so the org-branded page never shows a message nobody wrote.
 */
export const LOGIN_ERROR_MESSAGE_KEYS = {
  authentication_failed: 'errors.authenticationFailedRetry',
  session_expired: 'errors.sessionExpired',
  session_expired_privacy: 'errors.sessionExpiredPrivacy',
} as const

export type LoginErrorCode = keyof typeof LOGIN_ERROR_MESSAGE_KEYS

/** The `auth`-namespace message key for a `?error=` code, or null for anything unknown. */
export function loginErrorMessageKey(
  code: string | null | undefined
): (typeof LOGIN_ERROR_MESSAGE_KEYS)[LoginErrorCode] | null {
  if (!code || !Object.hasOwn(LOGIN_ERROR_MESSAGE_KEYS, code)) return null
  return LOGIN_ERROR_MESSAGE_KEYS[code as LoginErrorCode]
}

/**
 * Build a token-hash confirm URL — the cross-device, cookie-free path used by
 * password recovery (and, in time, other email-mediated flows). The link itself
 * carries `?token_hash=…`; this helper produces the URL we hand to Supabase as
 * `redirectTo`, which it substitutes for `{{ .ConfirmationURL }}` only when the
 * email template still references that variable. Once the template links
 * directly via `{{ .TokenHash }}`, this value is effectively cosmetic.
 */
export function getAuthConfirmUrl(type: AuthConfirmType, next: string): string {
  const base = getBaseUrl()
  const params = new URLSearchParams({ type, next })
  return `${base}/auth/confirm?${params.toString()}`
}
